import React from 'react';
import { Skeleton } from 'antd';

/**
 * کامپوننت‌های اسکلتون برای نمایش لودینگ در صفحات مختلف
 * 
 * مزایای استفاده از اسکلتون:
 * 1. UX بهتر: کاربر می‌داند چه محتوایی در حال لود است
 * 2. کاهش احساس انتظار
 * 3. حرفه‌ای‌تر به نظر می‌رسد
 * 4. می‌تواند ساختار صفحه را نشان دهد
 * 
 * نکته: برای لودینگ‌های سریع (کمتر از 200ms) استفاده از Spin ساده‌تر است
 */

/**
 * اسکلتون برای صفحات لیست (جداول) - ریسپانسیو با اسکرول افقی روی عرض کم
 */
export const TableSkeleton = ({ rows = 5, columns = 4 }) => {
  const cellMinWidth = 90;
  const gap = 12;
  const tableMinWidth = columns * cellMinWidth + (columns - 1) * gap;

  return (
    <div className="space-y-3 w-full min-w-0 overflow-hidden">
      {/* Header - ریسپانسیو */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Skeleton.Input active size="default" style={{ minWidth: 120, maxWidth: 200, height: 32, flex: '1 1 auto' }} />
        <div className="flex gap-2 flex-shrink-0">
          <Skeleton.Button active size="default" style={{ width: 100 }} />
          <Skeleton.Button active size="default" style={{ width: 100 }} />
        </div>
      </div>

      {/* جدول داخل اسکرول افقی تا از صفحه بیرون نزند */}
      <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
        <div style={{ minWidth: tableMinWidth }}>
          {/* Table Header */}
          <div className="flex gap-3 border-b border-gray-200 dark:border-gray-700 pb-3" style={{ minWidth: tableMinWidth }}>
            {Array.from({ length: columns }).map((_, i) => (
              <Skeleton.Input key={i} active size="default" style={{ width: cellMinWidth, minWidth: cellMinWidth, height: 20, flexShrink: 0 }} />
            ))}
          </div>

          {/* Table Rows */}
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="flex gap-3 py-3 border-b border-gray-100 dark:border-gray-800" style={{ minWidth: tableMinWidth }}>
              {Array.from({ length: columns }).map((_, j) => (
                <Skeleton.Input key={j} active size="default" style={{ width: cellMinWidth, minWidth: cellMinWidth, height: 20, flexShrink: 0 }} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * اسکلتون برای کارت‌های لیست (مثل لیست اقدام‌ها) - ریسپانسیو
 */
export const CardListSkeleton = ({ count = 3 }) => {
  return (
    <div className="space-y-4 w-full min-w-0 overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 min-w-0">
          <div className="flex flex-wrap items-start gap-4 min-w-0">
            <Skeleton.Avatar active size="large" shape="square" className="flex-shrink-0" />
            <div className="flex-1 space-y-2 min-w-0" style={{ minWidth: 0 }}>
              <Skeleton.Input active size="default" style={{ width: '100%', maxWidth: 280, height: 24 }} />
              <Skeleton.Input active size="default" style={{ width: '100%', maxWidth: 180, height: 20 }} />
              <div className="flex gap-2 flex-wrap">
                <Skeleton.Button active size="small" style={{ width: 60, height: 24 }} />
                <Skeleton.Button active size="small" style={{ width: 60, height: 24 }} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * اسکلتون برای فرم‌ها - ریسپانسیو
 */
export const FormSkeleton = ({ fields = 6 }) => {
  return (
    <div className="space-y-6 w-full min-w-0 overflow-hidden">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2 min-w-0">
          <Skeleton.Input active size="default" style={{ width: 120, maxWidth: '100%', height: 20 }} />
          <Skeleton.Input active size="large" style={{ width: '100%', maxWidth: '100%', height: 40, boxSizing: 'border-box' }} />
        </div>
      ))}
      <div className="flex flex-wrap gap-3 justify-end pt-4">
        <Skeleton.Button active size="default" style={{ width: 100, height: 40 }} />
        <Skeleton.Button active size="default" style={{ width: 100, height: 40 }} />
      </div>
    </div>
  );
};

/**
 * اسکلتون برای صفحات جزئیات (Detail Pages) - ریسپانسیو
 */
/** اسکلتون برای صفحه تنظیمات تیکت (هدر + تب‌ها + جدول) */
export const TicketSettingsSkeleton = () => {
  return (
    <div className="space-y-6 w-full min-w-0 overflow-hidden px-1">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <Skeleton.Avatar active size={48} shape="square" className="flex-shrink-0" />
          <div className="space-y-2 min-w-0">
            <Skeleton.Input active size="default" style={{ width: 200, maxWidth: '100%', height: 28 }} />
            <Skeleton.Input active size="small" style={{ width: 280, maxWidth: '100%', height: 16 }} />
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          <Skeleton.Button active size="default" style={{ width: 100 }} />
          <Skeleton.Button active size="default" style={{ width: 120 }} />
        </div>
      </div>
      <div className="overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="flex gap-2" style={{ minWidth: 'min-content' }}>
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton.Button key={i} active size="default" style={{ minWidth: 88, height: 36 }} />
          ))}
        </div>
      </div>
      <TableSkeleton rows={8} columns={4} />
    </div>
  );
};

export const DetailPageSkeleton = () => {
  return (
    <div className="space-y-6 w-full min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4 min-w-0">
          <Skeleton.Avatar active size={64} shape="square" className="flex-shrink-0" />
          <div className="space-y-2 min-w-0" style={{ minWidth: 0 }}>
            <Skeleton.Input active size="default" style={{ width: '100%', maxWidth: 200, height: 28 }} />
            <Skeleton.Input active size="default" style={{ width: '100%', maxWidth: 150, height: 20 }} />
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          <Skeleton.Button active size="default" style={{ width: 100 }} />
          <Skeleton.Button active size="default" style={{ width: 100 }} />
        </div>
      </div>

      {/* Tabs - اسکرول افقی در موبایل */}
      <div className="overflow-x-auto overflow-y-hidden min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700 pb-0" style={{ minWidth: 'min-content' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton.Input key={i} active size="default" style={{ width: 100, height: 40, flexShrink: 0 }} />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4 min-w-0">
        <Skeleton paragraph={{ rows: 4 }} active />
        <Skeleton paragraph={{ rows: 3 }} active />
      </div>
    </div>
  );
};

/**
 * اسکلتون محتوای تب‌های جزئیات پروژه
 */
export const WorkProjectTabSkeleton = ({ tab = 'overview' }) => {
  if (tab === 'overview') {
    return (
      <div className="space-y-4 w-full min-w-0">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
              <Skeleton.Input active size="small" style={{ width: 80, height: 14, marginBottom: 8 }} />
              <Skeleton.Input active size="default" style={{ width: '70%', height: 24 }} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700 min-h-[280px]">
            <Skeleton.Input active size="default" style={{ width: 120, height: 20, marginBottom: 16 }} />
            <Skeleton.Avatar active size={180} shape="circle" className="mx-auto block" />
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700 min-h-[280px]">
            <Skeleton.Input active size="default" style={{ width: 120, height: 20, marginBottom: 16 }} />
            <Skeleton paragraph={{ rows: 4 }} active />
          </div>
        </div>
      </div>
    );
  }

  if (tab === 'discussion') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full min-w-0">
        <CardListSkeleton count={4} />
        <div className="md:col-span-2">
          <CommentsSkeleton count={3} />
        </div>
      </div>
    );
  }

  if (tab === 'notes' || tab === 'activity') {
    return <CardListSkeleton count={5} />;
  }

  if (tab === 'rating') {
    return (
      <div className="max-w-lg space-y-4">
        <Skeleton paragraph={{ rows: 2 }} active />
        <Skeleton.Input active size="large" style={{ width: '100%', height: 80 }} />
      </div>
    );
  }

  return <TableSkeleton rows={6} columns={5} />;
};

/**
 * اسکلتون برای داشبورد (Dashboard Cards) - ریسپانسیو
 */
export const DashboardSkeleton = ({ cards = 6 }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full min-w-0">
      {Array.from({ length: cards }).map((_, i) => (
        <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 min-w-0 overflow-hidden">
          <div className="flex items-center justify-between mb-4 min-w-0">
            <Skeleton.Input active size="default" style={{ width: 120, maxWidth: '100%', height: 20 }} />
            <Skeleton.Avatar active size="small" shape="circle" className="flex-shrink-0" />
          </div>
          <Skeleton.Input active size="large" style={{ width: '60%', maxWidth: '100%', height: 32 }} />
          <Skeleton.Input active size="default" style={{ width: '40%', maxWidth: '100%', height: 16, marginTop: 8 }} />
        </div>
      ))}
    </div>
  );
};

/**
 * اسکلتون برای کامنت‌ها - ریسپانسیو
 */
export const CommentsSkeleton = ({ count = 3 }) => {
  return (
    <div className="space-y-4 w-full min-w-0 overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex gap-3 min-w-0">
          <Skeleton.Avatar active size="default" shape="circle" className="flex-shrink-0" />
          <div className="flex-1 space-y-2 min-w-0" style={{ minWidth: 0 }}>
            <div className="flex flex-wrap items-center gap-2">
              <Skeleton.Input active size="default" style={{ width: 100, maxWidth: '100%', height: 16 }} />
              <Skeleton.Input active size="default" style={{ width: 80, maxWidth: '100%', height: 16 }} />
            </div>
            <Skeleton paragraph={{ rows: 2 }} active />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * اسکلتون برای فیلتر پنل - ریسپانسیو
 */
export const FilterPanelSkeleton = () => {
  return (
    <div className="space-y-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 w-full min-w-0 overflow-hidden">
      <Skeleton.Input active size="default" style={{ width: 150, maxWidth: '100%', height: 24, marginBottom: 16 }} />
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2 min-w-0">
          <Skeleton.Input active size="default" style={{ width: 100, maxWidth: '100%', height: 16 }} />
          <Skeleton.Input active size="large" style={{ width: '100%', maxWidth: '100%', height: 40, boxSizing: 'border-box' }} />
        </div>
      ))}
      <div className="flex flex-wrap gap-2 pt-4">
        <Skeleton.Button active size="default" style={{ width: 100, height: 40 }} />
        <Skeleton.Button active size="default" style={{ width: 100, height: 40 }} />
      </div>
    </div>
  );
};

/**
 * Hook برای تصمیم‌گیری بین Spin و Skeleton
 * 
 * @param {number} loadingTime - زمان لودینگ به میلی‌ثانیه
 * @returns {boolean} - true اگر باید از Skeleton استفاده شود
 */
export const shouldUseSkeleton = (loadingTime) => {
  // اگر لودینگ بیشتر از 200ms طول بکشد، از Skeleton استفاده کن
  return loadingTime > 200;
};

export default {
  TableSkeleton,
  CardListSkeleton,
  FormSkeleton,
  DetailPageSkeleton,
  DashboardSkeleton,
  CommentsSkeleton,
  FilterPanelSkeleton,
  shouldUseSkeleton,
};
